"use client";

import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Layers,
  Loader2,
  Mail,
  RotateCcw,
  Settings,
  ShieldCheck,
  Terminal,
  Workflow,
  Zap,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackLeadSignup } from "@/lib/analytics";

interface ProductTourProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const tourSteps = [
  {
    id: 1,
    title: "Visual Orchestration",
    subtitle: "Design with precision",
    description:
      "Map out complex business logic with an intuitive drag-and-drop interface. Connect any API, database, or AI model in seconds.",
    icon: Layers,
  },
  {
    id: 2,
    title: "Enterprise Reliability",
    subtitle: "Built for scale",
    description:
      "Deploy workflows that never fail. Automatic retries, exponential backoff, and distributed execution come standard.",
    icon: ShieldCheck,
  },
  {
    id: 3,
    title: "Deep Observability",
    subtitle: "Full transparency",
    description:
      "Monitor every execution in real-time. Access granular logs, step-by-step replays, and instant error resolution tools.",
    icon: Activity,
  },
];

// Animated workflow preview for step 1
function WorkflowBuilder({ isActive }: { isActive: boolean }) {
  const [activeNodeIndex, setActiveNodeIndex] = useState(0);
  const [pulseIndex, setPulseIndex] = useState(0);

  // Use percentage-based coordinates for responsiveness
  // ViewBox is 500x200
  const nodes = [
    { id: 1, icon: Zap, label: "Trigger", x: 60, y: 100 },
    { id: 2, icon: Workflow, label: "Setup", x: 180, y: 100 },
    { id: 3, icon: Settings, label: "Logic", x: 300, y: 100 },
    { id: 4, icon: Database, label: "Storage", x: 420, y: 60 },
    { id: 5, icon: Mail, label: "Notify", x: 420, y: 140 },
  ];

  useEffect(() => {
    if (isActive) {
      const sequence = async () => {
        setActiveNodeIndex(0);
        setPulseIndex(0);

        // Path 1: Trigger -> Filter
        await new Promise((r) => setTimeout(r, 500));
        setPulseIndex(1);

        // Path 2: Filter -> Logic
        await new Promise((r) => setTimeout(r, 700));
        setActiveNodeIndex(1);
        setPulseIndex(2);

        // Path 3/4: Logic -> Storage/Notify
        await new Promise((r) => setTimeout(r, 700));
        setActiveNodeIndex(2);
        setPulseIndex(3); // This will trigger the split in the render logic

        await new Promise((r) => setTimeout(r, 700));
        setActiveNodeIndex(3);
      };
      sequence();
    } else {
      setActiveNodeIndex(-1);
      setPulseIndex(-1);
    }
  }, [isActive]);

  return (
    <div className="relative h-56 bg-muted/20 rounded-xl border border-border/50 overflow-hidden">
      <div className="absolute inset-0 pattern-grid opacity-10" />

      <div className="w-full h-full relative">
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 500 200"
          preserveAspectRatio="xMidYMid meet"
        >
          <motion.path
            d="M 100 112 L 180 112"
            stroke="currentColor"
            className={
              activeNodeIndex >= 1
                ? "text-primary transition-colors duration-500"
                : "text-primary/30"
            }
            strokeWidth="1.5"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={isActive ? { pathLength: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
          />
          <motion.path
            d="M 220 112 L 300 112"
            stroke="currentColor"
            className={
              activeNodeIndex >= 2
                ? "text-primary transition-colors duration-500"
                : "text-primary/30"
            }
            strokeWidth="1.5"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={isActive ? { pathLength: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.1 }}
          />
          <motion.path
            d="M 340 112 L 420 72"
            stroke="currentColor"
            className={
              activeNodeIndex >= 3
                ? "text-primary transition-colors duration-500"
                : "text-primary/30"
            }
            strokeWidth="1.5"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={isActive ? { pathLength: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.7 }}
          />
          <motion.path
            d="M 340 112 L 420 152"
            stroke="currentColor"
            className={
              activeNodeIndex >= 3
                ? "text-primary transition-colors duration-500"
                : "text-primary/30"
            }
            strokeWidth="1.5"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={isActive ? { pathLength: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.7 }}
          />
        </svg>

        {/* Execution Pulses (Smoothly moving along paths) */}
        {isActive && pulseIndex !== -1 && (
          <div className="absolute inset-0 w-full h-full left-1 top-7 z-[99] pointer-events-none">
            <svg
              className="w-full h-full"
              viewBox="0 0 500 200"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Pulse A: Always visible, goes to Storage at the end */}
              <motion.circle
                r="3"
                fill="currentColor"
                className="text-primary"
                animate={{
                  cx: nodes[pulseIndex <= 2 ? pulseIndex : 3].x + 20,
                  cy: nodes[pulseIndex <= 2 ? pulseIndex : 3].y - 20,
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  cx: { duration: 0.6, ease: "easeInOut" },
                  cy: { duration: 0.6, ease: "easeInOut" },
                  scale: { duration: 2, repeat: Infinity },
                }}
              />

              {/* Pulse B: Overlaps Pulse A until the Logic node, then splits to Notify */}
              <motion.circle
                r="3"
                fill="currentColor"
                className="text-primary"
                animate={{
                  cx: nodes[pulseIndex <= 2 ? pulseIndex : 4].x + 20,
                  cy: nodes[pulseIndex <= 2 ? pulseIndex : 4].y - 20,
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  cx: { duration: 0.6, ease: "easeInOut" },
                  cy: { duration: 0.6, ease: "easeInOut" },
                  scale: { duration: 2, repeat: Infinity },
                }}
              />
            </svg>
          </div>
        )}

        {nodes.map((node, index) => {
          // Node is "active" (lit up) if the process has reached or passed it
          const isNodeLit =
            (index <= 2 && activeNodeIndex >= index) ||
            (index >= 3 && activeNodeIndex === 3);

          // Calculate percentage positions based on 500x200 viewBox
          const left = `${(node.x / 500) * 100}%`;
          const top = `${(node.y / 200) * 100}%`;

          return (
            <motion.div
              key={node.id}
              className="absolute flex flex-col items-center gap-2"
              style={{ left, top, transform: "translate(-50%, -50%)" }}
              initial={{ opacity: 0, y: 10 }}
              animate={isActive ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.15 }}
            >
              <motion.div
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-background border shadow-sm flex items-center justify-center relative transition-all duration-500 ${
                  isNodeLit
                    ? "border-primary/50 shadow-lg shadow-primary/10"
                    : "border-border"
                }`}
                animate={isNodeLit ? { scale: 1.05 } : { scale: 1 }}
              >
                <node.icon
                  className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-500 ${isNodeLit ? "text-primary" : "text-foreground/70"}`}
                />

                {isActive && isNodeLit && (
                  <motion.div
                    className="absolute inset-0 rounded-xl bg-primary/5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  />
                )}
              </motion.div>
              <span
                className={`text-[8px] sm:text-[10px] font-medium uppercase tracking-wider transition-colors duration-500 ${
                  isNodeLit ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {node.label}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Animated execution preview for step 2
function ReliabilityPreview({ isActive }: { isActive: boolean }) {
  const [status, setStatus] = useState<
    "running" | "failed" | "retrying" | "success"
  >("running");
  const [attempt, setAttempt] = useState(1);

  useEffect(() => {
    if (isActive) {
      const sequence = async () => {
        setStatus("running");
        await new Promise((r) => setTimeout(r, 1500));
        setStatus("failed");
        await new Promise((r) => setTimeout(r, 2000));
        setStatus("retrying");
        setAttempt(2);
        await new Promise((r) => setTimeout(r, 2500));
        setStatus("success");
      };
      sequence();
    } else {
      setStatus("running");
      setAttempt(1);
    }
  }, [isActive]);

  return (
    <div className="h-56 bg-muted/20 rounded-xl border border-border/50 p-4 sm:p-6 flex flex-col justify-center relative overflow-hidden">
      <AnimatePresence>
        {status === "success" && (
          <motion.div
            initial={{ opacity: 0, y: 20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            className="absolute top-4 left-1/2 z-20 px-4 py-2 bg-success/10 border border-success/20 rounded-full flex items-center gap-2 shadow-sm"
          >
            <CheckCircle2 className="w-4 h-4 text-success" />
            <span className="text-xs font-medium text-success">
              Workflow Recovered Successfully
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-sm mx-auto w-full space-y-6 relative z-10 px-4 sm:px-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg bg-background border flex items-center justify-center transition-colors duration-500 ${
                status === "failed"
                  ? "border-error/50 bg-error/5"
                  : "border-border"
              }`}
            >
              {status === "running" && (
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              )}
              {status === "failed" && (
                <AlertTriangle className="w-5 h-5 text-error animate-pulse" />
              )}
              {status === "retrying" && (
                <RotateCcw className="w-5 h-5 text-amber-500 animate-spin" />
              )}
              {status === "success" && (
                <CheckCircle2 className="w-5 h-5 text-success" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {status === "running" && "Executing Node..."}
                {status === "failed" && "Connection Timeout"}
                {status === "retrying" && "Automatic Recovery"}
                {status === "success" && "Node Completed"}
              </p>
              <p className="text-xs text-muted-foreground">
                {status === "failed"
                  ? "API endpoint unreachable"
                  : `Attempt ${attempt} of 3`}
              </p>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={status}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              className={`px-2 py-1 rounded text-[10px] font-mono font-bold border ${
                status === "failed"
                  ? "bg-error/10 text-error border-error/20"
                  : status === "retrying"
                    ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                    : status === "success"
                      ? "bg-success/10 text-success border-success/20"
                      : "bg-primary/10 text-primary border-primary/20"
              }`}
            >
              {status.toUpperCase()}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>Exponential Backoff Strategy</span>
            <span>
              {status === "retrying"
                ? "2.4s delay"
                : status === "success"
                  ? "Recovered"
                  : "Monitoring..."}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-1">
                <div className="h-1 bg-background border border-border rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full ${
                      i < attempt
                        ? "bg-success"
                        : i === attempt
                          ? (status === "failed" ? "bg-error" : "bg-primary")
                          : "bg-muted"
                    }`}
                    initial={{ width: "0%" }}
                    animate={
                      isActive && i <= attempt
                        ? { width: "100%" }
                        : { width: "0%" }
                    }
                    transition={{ duration: 1.5, delay: (i - 1) * 0.5 }}
                  />
                </div>
                <div className="text-[8px] text-center text-muted-foreground font-mono">
                  T+{2 ** (i - 1)}s
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Animated debug preview for step 3
function ObservabilityPreview({ isActive }: { isActive: boolean }) {
  const [visibleLogs, setVisibleLogs] = useState<number>(0);
  const [isRecovered, setIsRecovered] = useState(false);

  const logs = [
    { time: "14:02:01.04", msg: "Incoming request validated", type: "info" },
    {
      time: "14:02:01.12",
      msg: "Executing transformation logic",
      type: "info",
    },
    {
      time: "14:02:02.45",
      msg: "Database write failed: Timeout",
      type: "error",
    },
    {
      time: "14:02:04.82",
      msg: "Retrying database write (Attempt 2)...",
      type: "warning",
    },
    { time: "14:02:05.12", msg: "Database write successful", type: "success" },
    {
      time: "14:02:05.58",
      msg: "Notification dispatched to Slack",
      type: "success",
    },
  ];

  useEffect(() => {
    if (isActive) {
      const sequence = async () => {
        setVisibleLogs(0);
        setIsRecovered(false);
        for (let i = 1; i <= logs.length; i++) {
          await new Promise((r) =>
            setTimeout(r, i === 3 || i === 4 ? 1500 : 800),
          );
          setVisibleLogs(i);
          if (i === 5) setIsRecovered(true);
        }
      };
      sequence();
    } else {
      setVisibleLogs(0);
      setIsRecovered(false);
    }
  }, [isActive]);

  return (
    <div className="h-56 bg-background rounded-xl border border-border/50 overflow-hidden flex flex-col shadow-sm relative">
      <AnimatePresence>
        {isRecovered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: "-50%" }}
            animate={{ opacity: 1, scale: 1, x: "-50%" }}
            className="absolute bottom-16 left-3/4 z-20 px-3 py-1.5 bg-background border border-primary/20 rounded-lg flex items-center gap-2 shadow-xl"
          >
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-[10px] font-medium text-foreground">
              Execution Trace Captured
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-[10px] font-mono text-muted-foreground">
            live-execution-stream
          </span>
        </div>
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-border" />
          <div className="w-1.5 h-1.5 rounded-full bg-border" />
        </div>
      </div>

      <div className="p-4 space-y-2.5 font-mono text-[10px] flex-1 overflow-y-auto overflow-x-auto">
        <AnimatePresence>
          {logs.slice(0, visibleLogs).map((log, index) => (
            <motion.div
              key={index}
              className="flex gap-4 min-w-max"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <span className="text-muted-foreground/40 shrink-0">
                {log.time}
              </span>
              <span
                className={`
                ${log.type === "success" ? "text-success" : ""}
                ${log.type === "error" ? "text-error" : ""}
                ${log.type === "warning" ? "text-amber-500" : ""}
                ${log.type === "info" ? "text-foreground/70" : ""}
              `}
              >
                {log.msg}
              </span>
            </motion.div>
          ))}
        </AnimatePresence>
        {isActive && visibleLogs < logs.length && (
          <motion.div
            className="w-1.5 h-3 bg-primary/40 ml-[72px]"
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          />
        )}
      </div>

      <div className="px-4 py-2 border-t border-border bg-muted/10 flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <div
            className={`w-2 h-2 rounded-full transition-colors duration-500 ${
              visibleLogs >= 3 && visibleLogs < 5
                ? "bg-error animate-pulse"
                : "bg-success"
            }`}
          />
          <span className="text-[9px] text-muted-foreground">
            {visibleLogs >= 3 && visibleLogs < 5
              ? "Error Detected"
              : "System Healthy"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-muted-foreground" />
          <span className="text-[9px] text-muted-foreground">
            Latency: {visibleLogs >= 3 ? "1.2s" : "142ms"}
          </span>
        </div>
      </div>
    </div>
  );
}

export function ProductTour({ open, onOpenChange }: ProductTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useRouter();
  const _isMobile = useIsMobile();

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onOpenChange(false);
      trackLeadSignup("landing_product_tour_complete");
      navigate.push("/register");
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
  };

  const step = tourSteps[currentStep];
  const StepIcon = step.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-2xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto p-0 bg-background border-border gap-0 shadow-2xl"
      >
        <div className="relative">
          <div className="p-6 sm:p-12">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-6 sm:mb-12">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    index === currentStep
                      ? "w-12 bg-primary"
                      : index < currentStep
                        ? "w-4 bg-primary/30"
                        : "w-4 bg-muted"
                  }`}
                />
              ))}
            </div>

            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {/* Header */}
                <div className="text-center mb-6 sm:mb-10">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/5 border border-primary/10 mb-4 sm:mb-6">
                    <StepIcon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 mb-2">
                    {step.subtitle}
                  </p>
                  <h2 className="text-2xl font-semibold text-foreground mb-4 tracking-tight">
                    {step.title}
                  </h2>
                  <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
                    {step.description}
                  </p>
                </div>

                {/* Preview Area */}
                <div className="mb-6 sm:mb-12">
                  {currentStep === 0 && <WorkflowBuilder isActive={open} />}
                  {currentStep === 1 && <ReliabilityPreview isActive={open} />}
                  {currentStep === 2 && (
                    <ObservabilityPreview isActive={open} />
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-border/50">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-muted-foreground hover:text-foreground"
              >
                Skip
              </Button>

              <div className="flex items-center gap-3">
                {currentStep > 0 && (
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentStep(currentStep - 1)}
                  >
                    Back
                  </Button>
                )}
                <Button onClick={handleNext} className="min-w-[120px]">
                  {currentStep === tourSteps.length - 1
                    ? "Get Started"
                    : "Continue"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
